import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Plus, Edit, Building2, Layers, Users, UserCog } from 'lucide-react';
import { RecordActions } from '../../../components/common/RecordActions';
import { RecordViewDialog } from '../../../components/common/RecordViewDialog';
import { useAuth } from '../../../context/AuthContext';
import { hcmAPI, companyAPI, masterDataAPI, timeManagementAPI } from '../../../services/api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Card } from '../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { toast } from 'sonner';

const EnterpriseStructure = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('enterprisestructure');
  const [viewItem, setViewItem] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Determine active tab from URL path
  const getActiveTabFromPath = () => {
    const path = location.pathname;
    if (path.includes('/personnel-subareas')) return 'personnel-subareas';
    if (path.includes('/employee-groups')) return 'employee-groups';
    if (path.includes('/employee-subgroups')) return 'employee-subgroups';
    return 'personnel-areas'; // default
  };
  
  const [activeTab, setActiveTab] = useState(getActiveTabFromPath());
  const [personnelAreas, setPersonnelAreas] = useState([]);
  const [personnelSubareas, setPersonnelSubareas] = useState([]);
  const [employeeGroups, setEmployeeGroups] = useState([]);
  const [employeeSubgroups, setEmployeeSubgroups] = useState([]);
  const [companyCodes, setCompanyCodes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [holidayCalendars, setHolidayCalendars] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formResetCounter, setFormResetCounter] = useState(0);
  
  // Form state for controlled components - Personnel Area
  const [selectedCompanyCode, setSelectedCompanyCode] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('EN');
  const [selectedStatus, setSelectedStatus] = useState('Active');
  const [selectedHolidayCalendar, setSelectedHolidayCalendar] = useState('');
  
  // Form state for controlled components - Personnel Subarea
  const [selectedPersonnelArea, setSelectedPersonnelArea] = useState('');
  const [selectedSubareaCountry, setSelectedSubareaCountry] = useState('');
  const [selectedSubareaStatus, setSelectedSubareaStatus] = useState('Active');
  
  // Form state for controlled components - Employee Group
  const [selectedEmployeeGroupStatus, setSelectedEmployeeGroupStatus] = useState('Active');
  
  // Form state for controlled components - Employee Subgroup
  const [selectedEmployeeGroupForSubgroup, setSelectedEmployeeGroupForSubgroup] = useState('');
  const [selectedEmployeeSubgroupCountry, setSelectedEmployeeSubgroupCountry] = useState('');
  const [selectedEmployeeSubgroupStatus, setSelectedEmployeeSubgroupStatus] = useState('Active');

  const tabs = [
    { id: 'personnel-areas', label: 'Personnel Areas', icon: Building2 },
    { id: 'personnel-subareas', label: 'Personnel Subareas', icon: Layers },
    { id: 'employee-groups', label: 'Employee Groups', icon: Users },
    { id: 'employee-subgroups', label: 'Employee Subgroups', icon: UserCog },
  ];

  useEffect(() => {
    loadMasterData();
  }, []);

  useEffect(() => {
    const newTab = getActiveTabFromPath();
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  // eslint-disable-next-line
  }, [location.pathname]);

  useEffect(() => {
    loadData();
  // eslint-disable-next-line
  }, [activeTab]);

  const loadMasterData = async () => {
    try {
      const [companyCodesRes, countriesRes, areasRes, groupsRes, holidayCalRes] = await Promise.all([
        companyAPI.getCompanyCodes(),
        masterDataAPI.getCountries(),
        hcmAPI.getPersonnelAreas(),
        hcmAPI.getEmployeeGroups(),
        timeManagementAPI.getHolidayCalendars().catch(() => ({ data: [] })),
      ]);
      setCompanyCodes(companyCodesRes.data);
      setCountries(countriesRes.data);
      setPersonnelAreas(areasRes.data);
      setEmployeeGroups(groupsRes.data);
      setHolidayCalendars(holidayCalRes.data || []);
    } catch (error) {
      console.error('Failed to load master data:', error);
      toast.error('Failed to load master data');
    }
  };

  const loadData = async () => {
    try {
      if (activeTab === 'personnel-areas') {
        const res = await hcmAPI.getPersonnelAreas();
        setPersonnelAreas(res.data);
      } else if (activeTab === 'personnel-subareas') {
        const [subareasRes, areasRes] = await Promise.all([
          hcmAPI.getPersonnelSubareas(),
          hcmAPI.getPersonnelAreas()
        ]);
        setPersonnelSubareas(subareasRes.data);
        setPersonnelAreas(areasRes.data);
      } else if (activeTab === 'employee-groups') {
        const res = await hcmAPI.getEmployeeGroups();
        setEmployeeGroups(res.data);
      } else if (activeTab === 'employee-subgroups') {
        const [subgroupsRes, groupsRes] = await Promise.all([
          hcmAPI.getEmployeeSubgroups(),
          hcmAPI.getEmployeeGroups()
        ]);
        setEmployeeSubgroups(subgroupsRes.data);
        setEmployeeGroups(groupsRes.data);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    navigate(`/hcm/config/enterprise-structure/${tabId}`);
  };

  const resetFormState = () => {
    setSelectedCompanyCode('');
    setSelectedCountry('');
    setSelectedLanguage('EN');
    setSelectedStatus('Active');
    setSelectedHolidayCalendar('');
    setSelectedPersonnelArea('');
    setSelectedSubareaCountry('');
    setSelectedSubareaStatus('Active');
    setSelectedEmployeeGroupStatus('Active');
    setSelectedEmployeeGroupForSubgroup('');
    setSelectedEmployeeSubgroupCountry('');
    setSelectedEmployeeSubgroupStatus('Active');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    // Clean up empty strings
    Object.keys(data).forEach(key => {
      if (data[key] === '' || data[key] === null || data[key] === undefined) {
        if (['description', 'region_code', 'address_street', 'po_box', 'postal_code', 'valid_to', 'work_schedule_grouping', 'holiday_calendar_id', 'default_payroll_area', 'default_schedule', 'time_quota_grouping', 'benefit_eligibility'].includes(key)) {
          delete data[key];
        }
      }
    });

    try {
      if (activeTab === 'personnel-areas') {
        if (editingItem) {
          await hcmAPI.updatePersonnelArea(editingItem.personnel_area_id, data);
          toast.success('Personnel Area updated successfully');
        } else {
          await hcmAPI.createPersonnelArea(data);
          toast.success('Personnel Area created successfully');
        }
      } else if (activeTab === 'personnel-subareas') {
        if (editingItem) {
          await hcmAPI.updatePersonnelSubarea(editingItem.personnel_area_id, editingItem.personnel_sub_area_id, data);
          toast.success('Personnel Subarea updated successfully');
        } else {
          await hcmAPI.createPersonnelSubarea(data);
          toast.success('Personnel Subarea created successfully');
        }
      } else if (activeTab === 'employee-groups') {
        if (editingItem) {
          await hcmAPI.updateEmployeeGroup(editingItem.employee_group_id, data);
          toast.success('Employee Group updated successfully');
        } else {
          await hcmAPI.createEmployeeGroup(data);
          toast.success('Employee Group created successfully');
        }
      } else if (activeTab === 'employee-subgroups') {
        if (editingItem) {
          await hcmAPI.updateEmployeeSubgroup(editingItem.employee_group_id, editingItem.employee_subgroup_id, data);
          toast.success('Employee Subgroup updated successfully');
        } else {
          await hcmAPI.createEmployeeSubgroup(data);
          toast.success('Employee Subgroup created successfully');
        }
      }
      
      setIsDialogOpen(false);
      setEditingItem(null);
      resetFormState();
      setFormResetCounter(prev => prev + 1);
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const firstError = detail[0];
        const fieldName = firstError?.loc?.slice(-1)[0] || 'field';
        toast.error(`${fieldName}: ${firstError?.msg || 'Validation error'}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Operation failed');
      }
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    
    if (activeTab === 'personnel-areas') {
      setSelectedCompanyCode(item.company_code_id || '');
      setSelectedCountry(item.country_key || '');
      setSelectedLanguage(item.language_key || 'EN');
      setSelectedStatus(item.status || 'Active');
      setSelectedHolidayCalendar(item.holiday_calendar_id || '');
    } else if (activeTab === 'personnel-subareas') {
      setSelectedPersonnelArea(item.personnel_area_id || '');
      setSelectedSubareaCountry(item.country_key || '');
      setSelectedSubareaStatus(item.status || 'Active');
    } else if (activeTab === 'employee-groups') {
      setSelectedEmployeeGroupStatus(item.status || 'Active');
    } else if (activeTab === 'employee-subgroups') {
      setSelectedEmployeeGroupForSubgroup(item.employee_group_id || '');
      setSelectedEmployeeSubgroupCountry(item.country_key || '');
      setSelectedEmployeeSubgroupStatus(item.status || 'Active');
    }
    
    setIsDialogOpen(true);
    setFormResetCounter(prev => prev + 1);
  };

  const getFormFields = () => {
    if (activeTab === 'personnel-areas') {
      return (
        <>
          <div>
            <Label htmlFor="personnel_area_id">Personnel Area ID *</Label>
            <Input id="personnel_area_id" name="personnel_area_id" placeholder="e.g., IN01, US01" maxLength={4} defaultValue={editingItem?.personnel_area_id || ''} disabled={!!editingItem} required />
            <p className="text-xs text-gray-500 mt-1">4 characters max, alphanumeric</p>
          </div>
          <div>
            <Label htmlFor="company_code_id">Company Code *</Label>
            <Select value={selectedCompanyCode} onValueChange={setSelectedCompanyCode} required>
              <SelectTrigger><SelectValue placeholder="Select Company Code" /></SelectTrigger>
              <SelectContent>
                {companyCodes.map(cc => (
                  <SelectItem key={cc.company_code_id} value={cc.company_code_id}>{cc.company_code_id} - {cc.company_code_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="hidden" name="company_code_id" value={selectedCompanyCode} />
          </div>
          <div>
            <Label htmlFor="name">Name *</Label>
            <Input id="name" name="name" placeholder="e.g., Bangalore Campus" maxLength={100} defaultValue={editingItem?.name || ''} required />
          </div>
          <div>
            <Label htmlFor="description">Description</Label>
            <Input id="description" name="description" placeholder="Long-form description" maxLength={255} defaultValue={editingItem?.description || ''} />
          </div>
          <div>
            <Label htmlFor="country_key">Country *</Label>
            <Select value={selectedCountry} onValueChange={setSelectedCountry} required>
              <SelectTrigger><SelectValue placeholder="Select Country" /></SelectTrigger>
              <SelectContent>
                {countries.map(country => (
                  <SelectItem key={country.country_code} value={country.country_code}>{country.country_code} - {country.country_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="hidden" name="country_key" value={selectedCountry} />
          </div>
          <div>
            <Label htmlFor="city">City *</Label>
            <Input id="city" name="city" placeholder="e.g., Bangalore" maxLength={100} defaultValue={editingItem?.city || ''} required />
          </div>
          <div>
            <Label htmlFor="region_code">Region/State Code</Label>
            <Input id="region_code" name="region_code" placeholder="e.g., KA, CA, TX" maxLength={10} defaultValue={editingItem?.region_code || ''} />
          </div>
          <div>
            <Label htmlFor="address_street">Street Address</Label>
            <Input id="address_street" name="address_street" placeholder="Street address" maxLength={255} defaultValue={editingItem?.address_street || ''} />
          </div>
          <div>
            <Label htmlFor="po_box">P.O. Box</Label>
            <Input id="po_box" name="po_box" placeholder="Postal box" maxLength={50} defaultValue={editingItem?.po_box || ''} />
          </div>
          <div>
            <Label htmlFor="postal_code">Postal Code</Label>
            <Input id="postal_code" name="postal_code" placeholder="e.g., 560100" maxLength={20} defaultValue={editingItem?.postal_code || ''} />
          </div>
          <div>
            <Label htmlFor="language_key">Language *</Label>
            <Select value={selectedLanguage} onValueChange={setSelectedLanguage} required>
              <SelectTrigger><SelectValue placeholder="Select Language" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="EN">EN - English</SelectItem>
                <SelectItem value="DE">DE - German</SelectItem>
                <SelectItem value="FR">FR - French</SelectItem>
                <SelectItem value="ES">ES - Spanish</SelectItem>
                <SelectItem value="HI">HI - Hindi</SelectItem>
                <SelectItem value="ZH">ZH - Chinese</SelectItem>
                <SelectItem value="JA">JA - Japanese</SelectItem>
              </SelectContent>
            </Select>
            <input type="hidden" name="language_key" value={selectedLanguage} />
          </div>
          <div>
            <Label htmlFor="status">Status *</Label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus} required>
              <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Blocked">Blocked</SelectItem>
              </SelectContent>
            </Select>
            <input type="hidden" name="status" value={selectedStatus} />
          </div>
          <div>
            <Label htmlFor="valid_from">Valid From *</Label>
            <Input type="date" id="valid_from" name="valid_from" defaultValue={editingItem?.valid_from || ''} required />
          </div>
          <div>
            <Label htmlFor="valid_to">Valid To</Label>
            <Input type="date" id="valid_to" name="valid_to" defaultValue={editingItem?.valid_to || ''} />
            <p className="text-xs text-gray-500 mt-1">Leave empty for open-ended validity</p>
          </div>
          <div className="col-span-2 border-t pt-4 mt-2">
            <h4 className="font-medium mb-3 text-sm text-gray-700">Time Management Configuration</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="holiday_calendar_id">Holiday Calendar</Label>
                <Select value={selectedHolidayCalendar} onValueChange={setSelectedHolidayCalendar}>
                  <SelectTrigger><SelectValue placeholder="Select Holiday Calendar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {holidayCalendars.filter(hc => hc.status === 'Active').map(hc => (
                      <SelectItem key={hc.holiday_calendar_id} value={hc.holiday_calendar_id}>
                        {hc.holiday_calendar_id} - {hc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <input type="hidden" name="holiday_calendar_id" value={selectedHolidayCalendar} />
                <p className="text-xs text-gray-500 mt-1">Used for absence calculation when employee has no Work Schedule Rule</p>
              </div>
            </div>
          </div>
        </>
      );
    } else if (activeTab === 'personnel-subareas') {
      return (
        <>
          <div>
            <Label htmlFor="personnel_sub_area_id">Personnel Subarea ID *</Label>
            <Input id="personnel_sub_area_id" name="personnel_sub_area_id" placeholder="e.g., AP01, H001" maxLength={4} defaultValue={editingItem?.personnel_sub_area_id || ''} disabled={!!editingItem} required />
          </div>
          <div>
            <Label htmlFor="personnel_area_id">Personnel Area *</Label>
            <Select value={selectedPersonnelArea} onValueChange={setSelectedPersonnelArea} required>
              <SelectTrigger><SelectValue placeholder="Select Personnel Area" /></SelectTrigger>
              <SelectContent>
                {personnelAreas.filter(pa => pa.status === 'Active').map(pa => (
                  <SelectItem key={pa.personnel_area_id} value={pa.personnel_area_id}>{pa.personnel_area_id} - {pa.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="hidden" name="personnel_area_id" value={selectedPersonnelArea} />
          </div>
          <div>
            <Label htmlFor="name">Name *</Label>
            <Input id="name" name="name" placeholder="e.g., Accounts Dept" maxLength={100} defaultValue={editingItem?.name || ''} required />
          </div>
          <div>
            <Label htmlFor="description">Description</Label>
            <Input id="description" name="description" placeholder="Full description" maxLength={255} defaultValue={editingItem?.description || ''} />
          </div>
          <div>
            <Label htmlFor="country_key">Country *</Label>
            <Select value={selectedSubareaCountry} onValueChange={setSelectedSubareaCountry} required>
              <SelectTrigger><SelectValue placeholder="Select Country" /></SelectTrigger>
              <SelectContent>
                {countries.map(country => (
                  <SelectItem key={country.country_code} value={country.country_code}>{country.country_code} - {country.country_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="hidden" name="country_key" value={selectedSubareaCountry} />
          </div>
          <div>
            <Label htmlFor="region_code">Region/State Code</Label>
            <Input id="region_code" name="region_code" placeholder="e.g., KA" maxLength={10} defaultValue={editingItem?.region_code || ''} />
          </div>
          <div>
            <Label htmlFor="work_schedule_grouping">Work Schedule Grouping</Label>
            <Input id="work_schedule_grouping" name="work_schedule_grouping" placeholder="e.g., WAGE1" maxLength={50} defaultValue={editingItem?.work_schedule_grouping || ''} />
          </div>
          <div>
            <Label htmlFor="holiday_calendar_id">Holiday Calendar ID</Label>
            <Input id="holiday_calendar_id" name="holiday_calendar_id" placeholder="e.g., IN_HC" maxLength={20} defaultValue={editingItem?.holiday_calendar_id || ''} />
          </div>
          <div>
            <Label htmlFor="status">Status *</Label>
            <Select value={selectedSubareaStatus} onValueChange={setSelectedSubareaStatus} required>
              <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Blocked">Blocked</SelectItem>
              </SelectContent>
            </Select>
            <input type="hidden" name="status" value={selectedSubareaStatus} />
          </div>
          <div>
            <Label htmlFor="valid_from">Valid From *</Label>
            <Input type="date" id="valid_from" name="valid_from" defaultValue={editingItem?.valid_from || ''} required />
          </div>
          <div>
            <Label htmlFor="valid_to">Valid To</Label>
            <Input type="date" id="valid_to" name="valid_to" defaultValue={editingItem?.valid_to || ''} />
          </div>
        </>
      );
    } else if (activeTab === 'employee-groups') {
      return (
        <>
          <div>
            <Label htmlFor="employee_group_id">Employee Group ID *</Label>
            <Input id="employee_group_id" name="employee_group_id" placeholder="e.g., 1, 2, A" maxLength={2} defaultValue={editingItem?.employee_group_id || ''} disabled={!!editingItem} required />
          </div>
          <div>
            <Label htmlFor="name">Name *</Label>
            <Input id="name" name="name" placeholder="e.g., Active Employees" maxLength={100} defaultValue={editingItem?.name || ''} required />
          </div>
          <div>
            <Label htmlFor="description">Description</Label>
            <Input id="description" name="description" placeholder="Extended info" maxLength={255} defaultValue={editingItem?.description || ''} />
          </div>
          <div>
            <Label htmlFor="status">Status *</Label>
            <Select value={selectedEmployeeGroupStatus} onValueChange={setSelectedEmployeeGroupStatus} required>
              <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Blocked">Blocked</SelectItem>
              </SelectContent>
            </Select>
            <input type="hidden" name="status" value={selectedEmployeeGroupStatus} />
          </div>
          <div>
            <Label htmlFor="default_payroll_area">Default Payroll Area</Label>
            <Input id="default_payroll_area" name="default_payroll_area" placeholder="e.g., A1" maxLength={10} defaultValue={editingItem?.default_payroll_area || ''} />
          </div>
          <div>
            <Label htmlFor="default_schedule">Default Schedule</Label>
            <Input id="default_schedule" name="default_schedule" placeholder="e.g., STD" maxLength={50} defaultValue={editingItem?.default_schedule || ''} />
          </div>
        </>
      );
    } else if (activeTab === 'employee-subgroups') {
      return (
        <>
          <div>
            <Label htmlFor="employee_subgroup_id">Employee Subgroup ID *</Label>
            <Input id="employee_subgroup_id" name="employee_subgroup_id" placeholder="e.g., S, H" maxLength={2} defaultValue={editingItem?.employee_subgroup_id || ''} disabled={!!editingItem} required />
          </div>
          <div>
            <Label htmlFor="employee_group_id">Employee Group *</Label>
            <Select value={selectedEmployeeGroupForSubgroup} onValueChange={setSelectedEmployeeGroupForSubgroup} required>
              <SelectTrigger><SelectValue placeholder="Select Employee Group" /></SelectTrigger>
              <SelectContent>
                {employeeGroups.filter(eg => eg.status === 'Active').map(eg => (
                  <SelectItem key={eg.employee_group_id} value={eg.employee_group_id}>{eg.employee_group_id} - {eg.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="hidden" name="employee_group_id" value={selectedEmployeeGroupForSubgroup} />
          </div>
          <div>
            <Label htmlFor="name">Name *</Label>
            <Input id="name" name="name" placeholder="e.g., Salaried" maxLength={100} defaultValue={editingItem?.name || ''} required />
          </div>
          <div>
            <Label htmlFor="description">Description</Label>
            <Input id="description" name="description" placeholder="Extended description" maxLength={255} defaultValue={editingItem?.description || ''} />
          </div>
          <div>
            <Label htmlFor="country_key">Country</Label>
            <Select value={selectedEmployeeSubgroupCountry} onValueChange={setSelectedEmployeeSubgroupCountry}>
              <SelectTrigger><SelectValue placeholder="Select Country (optional)" /></SelectTrigger>
              <SelectContent>
                {countries.map(country => (
                  <SelectItem key={country.country_code} value={country.country_code}>{country.country_code} - {country.country_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="hidden" name="country_key" value={selectedEmployeeSubgroupCountry} />
          </div>
          <div>
            <Label htmlFor="status">Status *</Label>
            <Select value={selectedEmployeeSubgroupStatus} onValueChange={setSelectedEmployeeSubgroupStatus} required>
              <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Blocked">Blocked</SelectItem>
              </SelectContent>
            </Select>
            <input type="hidden" name="status" value={selectedEmployeeSubgroupStatus} />
          </div>
          <div>
            <Label htmlFor="default_payroll_area">Default Payroll Area</Label>
            <Input id="default_payroll_area" name="default_payroll_area" placeholder="e.g., A1" maxLength={10} defaultValue={editingItem?.default_payroll_area || ''} />
          </div>
          <div>
            <Label htmlFor="default_schedule">Default Schedule</Label>
            <Input id="default_schedule" name="default_schedule" placeholder="e.g., STD" maxLength={50} defaultValue={editingItem?.default_schedule || ''} />
          </div>
          <div>
            <Label htmlFor="time_quota_grouping">Time Quota Grouping</Label>
            <Input id="time_quota_grouping" name="time_quota_grouping" placeholder="e.g., MONTHLY" maxLength={50} defaultValue={editingItem?.time_quota_grouping || ''} />
          </div>
          <div>
            <Label htmlFor="benefit_eligibility">Benefit Eligibility</Label>
            <Input id="benefit_eligibility" name="benefit_eligibility" placeholder="e.g., FULL" maxLength={50} defaultValue={editingItem?.benefit_eligibility || ''} />
          </div>
        </>
      );
    }
  };

  const getTableContent = () => {
    let data, headers, rowRenderer;

    if (activeTab === 'personnel-areas') {
      data = personnelAreas;
      headers = ['ID', 'Name', 'Company Code', 'City', 'Holiday Calendar', 'Status', 'Actions'];
      rowRenderer = (item) => (
        <tr key={item.personnel_area_id} className="border-b border-gray-100 hover:bg-gray-50">
          <td className="py-3 px-4 font-mono text-sm font-semibold">{item.personnel_area_id}</td>
          <td className="py-3 px-4">
            <div className="text-sm font-medium">{item.name}</div>
            {item.description && <div className="text-xs text-gray-600">{item.description}</div>}
          </td>
          <td className="py-3 px-4 font-mono text-sm">{item.company_code_id}</td>
          <td className="py-3 px-4 text-sm">{item.city}, {item.country_key}</td>
          <td className="py-3 px-4 text-sm">
            {item.holiday_calendar_id ? (
              <span className="px-2 py-1 bg-orange-50 text-orange-700 rounded text-xs">{item.holiday_calendar_id}</span>
            ) : (
              <span className="text-gray-400 text-xs">Not Set</span>
            )}
          </td>
          <td className="py-3 px-4">
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${item.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {item.status}
            </span>
          </td>
          <td className="py-3 px-4">
            <RecordActions doctype="enterprisestructure" onView={() => setViewItem(item)} onEdit={() => handleEdit(item)} />
          </td>
        </tr>
      );
    } else if (activeTab === 'personnel-subareas') {
      data = personnelSubareas;
      headers = ['Subarea ID', 'Name', 'Personnel Area', 'Work Schedule', 'Holiday Cal', 'Country', 'Status', 'Actions'];
      rowRenderer = (item) => (
        <tr key={`${item.personnel_area_id}-${item.personnel_sub_area_id}`} className="border-b border-gray-100 hover:bg-gray-50">
          <td className="py-3 px-4 font-mono text-sm font-semibold">{item.personnel_sub_area_id}</td>
          <td className="py-3 px-4">
            <div className="text-sm font-medium">{item.name}</div>
            {item.description && <div className="text-xs text-gray-600">{item.description}</div>}
          </td>
          <td className="py-3 px-4 font-mono text-sm">{item.personnel_area_id}</td>
          <td className="py-3 px-4 text-sm">{item.work_schedule_grouping || '-'}</td>
          <td className="py-3 px-4 text-sm">{item.holiday_calendar_id || '-'}</td>
          <td className="py-3 px-4 font-mono text-sm">{item.country_key}</td>
          <td className="py-3 px-4">
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${item.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {item.status}
            </span>
          </td>
          <td className="py-3 px-4">
            <RecordActions doctype="enterprisestructure" onView={() => setViewItem(item)} onEdit={() => handleEdit(item)} />
          </td>
        </tr>
      );
    } else if (activeTab === 'employee-groups') {
      data = employeeGroups;
      headers = ['ID', 'Name', 'Description', 'Status', 'Defaults', 'Actions'];
      rowRenderer = (item) => (
        <tr key={item.employee_group_id} className="border-b border-gray-100 hover:bg-gray-50">
          <td className="py-3 px-4 font-mono text-sm font-semibold">{item.employee_group_id}</td>
          <td className="py-3 px-4 text-sm font-medium">{item.name}</td>
          <td className="py-3 px-4 text-sm text-gray-600">{item.description || '-'}</td>
          <td className="py-3 px-4">
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${item.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {item.status}
            </span>
          </td>
          <td className="py-3 px-4 text-xs">
            {item.default_payroll_area && <div>Payroll: {item.default_payroll_area}</div>}
            {item.default_schedule && <div>Schedule: {item.default_schedule}</div>}
            {!item.default_payroll_area && !item.default_schedule && '-'}
          </td>
          <td className="py-3 px-4">
            <RecordActions doctype="enterprisestructure" onView={() => setViewItem(item)} onEdit={() => handleEdit(item)} />
          </td>
        </tr>
      );
    } else if (activeTab === 'employee-subgroups') {
      data = employeeSubgroups;
      headers = ['Group', 'Subgroup', 'Name', 'Country', 'Status', 'Overrides', 'Actions'];
      rowRenderer = (item) => (
        <tr key={`${item.employee_group_id}-${item.employee_subgroup_id}`} className="border-b border-gray-100 hover:bg-gray-50">
          <td className="py-3 px-4 font-mono text-sm">{item.employee_group_id}</td>
          <td className="py-3 px-4 font-mono text-sm font-semibold">{item.employee_subgroup_id}</td>
          <td className="py-3 px-4">
            <div className="text-sm font-medium">{item.name}</div>
            {item.description && <div className="text-xs text-gray-600">{item.description}</div>}
          </td>
          <td className="py-3 px-4 font-mono text-sm">{item.country_key || '-'}</td>
          <td className="py-3 px-4">
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${item.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {item.status}
            </span>
          </td>
          <td className="py-3 px-4 text-xs">
            {item.default_payroll_area && <div>Payroll: {item.default_payroll_area}</div>}
            {item.default_schedule && <div>Schedule: {item.default_schedule}</div>}
            {item.time_quota_grouping && <div>Quota: {item.time_quota_grouping}</div>}
            {!item.default_payroll_area && !item.default_schedule && !item.time_quota_grouping && '-'}
          </td>
          <td className="py-3 px-4">
            <RecordActions doctype="enterprisestructure" onView={() => setViewItem(item)} onEdit={() => handleEdit(item)} />
          </td>
        </tr>
      );
    }

    return { data, headers, rowRenderer };
  };

  const { data, headers, rowRenderer } = getTableContent();

  const getDialogTitle = () => {
    const action = editingItem ? 'Edit' : 'Create';
    const entity = activeTab === 'personnel-areas' ? 'Personnel Area' :
                   activeTab === 'personnel-subareas' ? 'Personnel Subarea' :
                   activeTab === 'employee-groups' ? 'Employee Group' : 'Employee Subgroup';
    return `${action} ${entity}`;
  };

  return (
    <Layout>
      <div className="p-6" data-testid="enterprise-structure-page">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm')}>HCM</span>
            <span>/</span>
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm/config/enterprise-structure')}>Configuration</span>
            <span>/</span>
            <span>Enterprise Structure</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Enterprise Structure Configuration</h1>
          <p className="text-gray-600 mt-1">Manage personnel areas, subareas, and employee groupings</p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 pb-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content Card */}
        <Card className="p-0 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
            <h2 className="font-semibold text-gray-900">
              {tabs.find(t => t.id === activeTab)?.label}
            </h2>
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) {
                setEditingItem(null);
                resetFormState();
              }
            }}>
              <DialogTrigger asChild>
                {writable && (<Button size="sm" data-testid="create-btn">
                  <Plus className="h-4 w-4 mr-1" />
                  Create
                </Button>)}
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{getDialogTitle()}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} key={formResetCounter} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    {getFormFields()}
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" data-testid="submit-btn">{editingItem ? 'Update' : 'Create'}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {headers?.map((header, idx) => (
                    <th key={idx} className="text-left py-3 px-4 text-sm font-semibold text-gray-700">{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data?.length > 0 ? (
                  data.map(rowRenderer)
                ) : (
                  <tr>
                    <td colSpan={headers?.length || 1} className="py-8 text-center text-gray-500">
                      No records found. Click "Create" to add a new entry.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default EnterpriseStructure;
